import { http, HttpResponse, delay } from 'msw';

import {
  Location as LocationDto,
  SiteCreateRequest as SiteCreateRequestDto,
  SiteWithAddress as SiteWithAddressDto,
} from '@electricjs/core_entity-client';

import {
  mockLocations,
  mockOrganizationId,
  mockSites,
} from '@/mocks/data/siteData';

// Light in-memory state so a site/location created in the UI shows up in the
// follow-up list fetch.
const sites: SiteWithAddressDto[] = [...mockSites];
const locations: LocationDto[] = [...mockLocations];

export const siteHandlers = [
  http.get('*/v1/organizations/:organizationId/sites', async () => {
    const results = sites.filter(site => !site.archived_at);
    await delay();
    return HttpResponse.json(
      { results, total: results.length },
      { status: 200 }
    );
  }),

  http.post('*/v1/organizations/:organizationId/sites', async ({ request }) => {
    const body = (await request.json()) as SiteCreateRequestDto;
    const site: SiteWithAddressDto = {
      id: crypto.randomUUID(),
      name: body.name,
      phone_number: body.phone_number,
      organization_id: mockOrganizationId,
      is_archivable: true,
      address: {
        street_1: body.street_1,
        street_2: body.street_2,
        city: body.city,
        state: body.state,
        zip_code: body.zip_code,
        country: body.country,
      },
    };
    sites.push(site);
    await delay();
    return HttpResponse.json(site, { status: 201 });
  }),

  http.post(
    '*/v1/organizations/:organizationId/sites/:siteId/archive',
    async ({ params }) => {
      const site = sites.find(({ id }) => id === params.siteId);
      if (!site) {
        return new HttpResponse(null, { status: 404 });
      }
      site.archived_at = new Date().toISOString();
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/sites/:siteId',
    async ({ params }) => {
      const site = sites.find(({ id }) => id === params.siteId);
      await delay();
      return site
        ? HttpResponse.json(site, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),

  http.get('*/v1/sites', async () => {
    await delay();
    return HttpResponse.json(sites, { status: 200 });
  }),

  http.get('*/v1/sites/:siteId/locations', async ({ params }) => {
    await delay();
    return HttpResponse.json(
      locations.filter(location => location.site_id === params.siteId),
      { status: 200 }
    );
  }),

  http.post('*/v1/sites/:siteId/locations', async ({ request, params }) => {
    const { name } = (await request.json()) as { name: string };
    const location: LocationDto = {
      id: crypto.randomUUID(),
      site_id: params.siteId as string,
      name,
    };
    locations.push(location);
    await delay();
    return HttpResponse.json(location, { status: 201 });
  }),
];
